import { createTheme, alpha } from '@mui/material/styles';

export const tokens = {
  colors: {
    primary: {
      50: '#eef2fb',
      100: '#d5dfef',
      200: '#a8bade',
      300: '#7a95cd',
      400: '#4d70bc',
      500: '#1b3f87', // brand anchor
      600: '#163370',
      700: '#102658',
      800: '#0b1a41',
      900: '#070d2a',
    },
    neutral: {
      50: '#ffffff',
      100: '#f1f5f9',
      200: '#e2e8f0',
      300: '#cbd5e1',
      400: '#94a3b8',
      500: '#64748b',
      600: '#475569',
      700: '#334155',
      800: '#1e293b',
      900: '#0f172a',
    },
    success: { main: '#15803d', light: '#dcfce7', contrastText: '#fff' },
    warning: { main: '#b45309', light: '#fef3c7', contrastText: '#fff' },
    error: { main: '#b91c1c', light: '#fee2e2', contrastText: '#fff' },
    info: { main: '#0369a1', light: '#e0f2fe', contrastText: '#fff' },
  },
  table: {
    headerBg: '#e2e8f0',
    headerText: '#334155',
    cellBorder: '#f1f5f9',
    headerBorder: '#e2e8f0',
    rowHover: alpha('#1b3f87', 0.03),
    rowSelected: alpha('#1b3f87', 0.06),
    rowDisabled: '#f1f5f9',
    stripedRow: '#fafbfc',
  },
  radius: {
    xs: 4,
    sm: 6,
    md: 8,
    lg: 10,
    xl: 14,
  },
};

export const field = {
  fontSize: '0.8125rem',
  lineHeight: 1.55,
  paddingY: '8px',
  paddingX: '12px',
  helperFontSize: '0.72rem',
  helperLineHeight: 1.4,
  helperMarginTop: '4px',
  placeholderColor: tokens.colors.neutral[400],
};

// ─────────────────────────────────────────────────────────────────────────────
// Theme
// ─────────────────────────────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    primary: {
      main: tokens.colors.primary[500],
      light: tokens.colors.primary[300],
      dark: tokens.colors.primary[700],
      50: tokens.colors.primary[50],
      contrastText: '#ffffff',
    },
    secondary: {
      main: tokens.colors.neutral[600],
      light: tokens.colors.neutral[400],
      dark: tokens.colors.neutral[800],
      contrastText: '#ffffff',
    },
    error: { main: tokens.colors.error.main, light: tokens.colors.error.light },
    warning: { main: tokens.colors.warning.main, light: tokens.colors.warning.light },
    success: { main: tokens.colors.success.main, light: tokens.colors.success.light },
    info: { main: tokens.colors.info.main, light: tokens.colors.info.light },
    background: {
      default: tokens.colors.neutral[50],
      paper: '#ffffff',
    },
    text: {
      primary: tokens.colors.neutral[800],
      secondary: tokens.colors.neutral[500],
      disabled: tokens.colors.neutral[400],
    },
    divider: tokens.colors.neutral[300],
    action: {
      hover: alpha(tokens.colors.primary[500], 0.04),
      selected: alpha(tokens.colors.primary[500], 0.08),
      disabled: tokens.colors.neutral[300],
    },
    grey: tokens.colors.neutral,
  },

  // ── Typography ──────────────────────────────────────────────────────────
  typography: {
    fontFamily: '"DM Sans", "Helvetica Neue", Arial, sans-serif',
    h1: { fontWeight: 600, letterSpacing: '-0.025em', lineHeight: 1.2 },
    h2: { fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.25 },
    h3: { fontWeight: 600, letterSpacing: '-0.015em', lineHeight: 1.3 },
    h4: { fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.35 },
    h5: { fontWeight: 600, lineHeight: 1.4 },
    h6: { fontWeight: 600, lineHeight: 1.4 },
    body1: { fontSize: '0.875rem', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', lineHeight: 1.55 },
    caption: { fontSize: '0.75rem', color: tokens.colors.neutral[500], lineHeight: 1.4 },
    overline: {
      fontSize: '0.6875rem',
      fontWeight: 600,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      lineHeight: 1.4,
    },
    button: { textTransform: 'none', fontWeight: 500 },
  },

  shape: { borderRadius: tokens.radius.md },

  // ── Shadows ─────────────────────────────────────────────────────────────
  shadows: [
    'none',
    '0 1px 2px rgba(15,23,42,0.06), 0 1px 3px rgba(15,23,42,0.08)',
    '0 1px 3px rgba(15,23,42,0.08), 0 2px 6px rgba(15,23,42,0.06)',
    '0 2px 6px rgba(15,23,42,0.08), 0 4px 12px rgba(15,23,42,0.06)',
    '0 4px 10px rgba(15,23,42,0.08), 0 6px 16px rgba(15,23,42,0.06)',
    '0 6px 16px rgba(15,23,42,0.08), 0 8px 20px rgba(15,23,42,0.06)',
    '0 8px 20px rgba(15,23,42,0.10)',
    '0 10px 24px rgba(15,23,42,0.10)',
    '0 12px 28px rgba(15,23,42,0.12)',
    '0 14px 32px rgba(15,23,42,0.12)',
    '0 16px 36px rgba(15,23,42,0.12)',
    '0 18px 40px rgba(15,23,42,0.12)',
    '0 20px 44px rgba(15,23,42,0.14)',
    '0 22px 48px rgba(15,23,42,0.14)',
    '0 24px 52px rgba(15,23,42,0.14)',
    '0 26px 56px rgba(15,23,42,0.14)',
    '0 28px 60px rgba(15,23,42,0.16)',
    '0 30px 64px rgba(15,23,42,0.16)',
    '0 32px 68px rgba(15,23,42,0.16)',
    '0 34px 72px rgba(15,23,42,0.16)',
    '0 36px 76px rgba(15,23,42,0.18)',
    '0 38px 80px rgba(15,23,42,0.18)',
    '0 40px 84px rgba(15,23,42,0.18)',
    '0 42px 88px rgba(15,23,42,0.20)',
    '0 44px 92px rgba(15,23,42,0.20)',
  ],

  // ── Component Overrides ─────────────────────────────────────────────────
  components: {

    // Global: DM Sans font import
    MuiCssBaseline: {
      styleOverrides: `
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
        *, *::before, *::after { box-sizing: border-box; }
        body { background-color: ${tokens.colors.neutral[50]}; }
        a { color: ${tokens.colors.primary[500]}; text-decoration: none; }
        a:hover { text-decoration: underline; }
      `,
    },

    // ── Table ──────────────────────────────────────────────────────────────
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.lg,
          border: `1px solid ${tokens.table.headerBorder}`,
          boxShadow: '0 1px 3px rgba(15,23,42,0.06), 0 1px 6px rgba(15,23,42,0.04)',
          overflowX: 'auto',
          backgroundColor: '#ffffff',
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: { borderCollapse: 'separate', borderSpacing: 0, minWidth: 800 },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: tokens.table.headerBg,
            color: tokens.table.headerText,
            fontWeight: 600,
            fontSize: '0.6875rem',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            padding: '11px 16px',
            borderBottom: `2px solid ${tokens.table.headerBorder}`,
            whiteSpace: 'nowrap',
            userSelect: 'none',
            lineHeight: 1.4,
          },
          '& .MuiTableCell-paddingCheckbox': {
            padding: '0 4px 0 12px',
            borderBottom: `2px solid ${tokens.table.headerBorder}`,
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root': {
            transition: 'background-color 0.12s ease',
            '&:hover': { backgroundColor: tokens.table.rowHover },
            '&:last-child .MuiTableCell-body': { borderBottom: 'none' },
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: tokens.table.rowSelected,
            '&:hover': { backgroundColor: alpha(tokens.colors.primary[500], 0.09) },
          },
          '&.row-disabled': {
            backgroundColor: tokens.table.rowDisabled,
            opacity: 0.65,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '10px 16px',
          fontSize: '0.8125rem',
          color: tokens.colors.neutral[700],
          borderBottom: `1px solid ${tokens.table.cellBorder}`,
          lineHeight: 1.5,
        },
        head: {
          position: 'sticky',
          top: 0,
          zIndex: 2,
        },
        paddingCheckbox: {
          padding: '0 4px 0 12px',
        },
      },
    },

    // ── Checkbox ────────────────────────────────────────────────────────────
    MuiCheckbox: {
      defaultProps: { disableRipple: true },
      styleOverrides: {
        root: {
          padding: 4,
          color: tokens.colors.neutral[400],
          borderRadius: tokens.radius.xs,
          transition: 'color 0.12s ease',
          '&.Mui-checked': { color: tokens.colors.primary[500] },
          '&.MuiCheckbox-indeterminate': { color: tokens.colors.primary[500] },
          '&.Mui-disabled': { color: tokens.colors.neutral[300] },
          '&:hover': { backgroundColor: alpha(tokens.colors.primary[500], 0.06) },
        },
      },
    },

    // ── Pagination ──────────────────────────────────────────────────────────
    MuiPagination: {
      styleOverrides: {
        root: { display: 'flex', justifyContent: 'center' },
        ul: { gap: 2 },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.sm,
          fontSize: '0.8125rem',
          fontWeight: 500,
          minWidth: 32,
          height: 32,
          color: tokens.colors.neutral[600],
          border: '1px solid transparent',
          transition: 'all 0.15s ease',
          '&:hover': {
            backgroundColor: alpha(tokens.colors.primary[500], 0.06),
            borderColor: alpha(tokens.colors.primary[500], 0.25),
            color: tokens.colors.primary[500],
          },
          '&.Mui-selected': {
            backgroundColor: tokens.colors.primary[500],
            color: '#ffffff',
            borderColor: tokens.colors.primary[500],
            fontWeight: 600,
            '&:hover': { backgroundColor: tokens.colors.primary[600] },
          },
          '&.Mui-disabled': { color: tokens.colors.neutral[300] },
        },
        ellipsis: { border: 'none' },
        previousNext: { border: `1px solid ${tokens.colors.neutral[200]}` },
      },
    },

    // ── Buttons ─────────────────────────────────────────────────────────────
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.sm,
          fontWeight: 500,
          fontSize: '0.8125rem',
          textTransform: 'none',
          transition: 'all 0.15s ease',
          lineHeight: 1.5,
        },
        sizeSmall: { padding: '4px 10px', fontSize: '0.75rem' },
        sizeMedium: { padding: '7px 16px',
          minHeight: 40,
          height: 40,
        },
        sizeLarge: { padding: '10px 22px', fontSize: '0.9375rem' },
        contained: {
          '&:hover': { boxShadow: `0 2px 8px ${alpha(tokens.colors.primary[500], 0.28)}` },
        },
        outlined: {
          borderColor: tokens.colors.neutral[300],
          '&:hover': {
            borderColor: tokens.colors.primary[400],
            backgroundColor: alpha(tokens.colors.primary[500], 0.04),
          },
        },
        text: {
          '&:hover': { backgroundColor: alpha(tokens.colors.primary[500], 0.06) },
        },
      },
    },

    // ── IconButton ──────────────────────────────────────────────────────────
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.sm,
          transition: 'all 0.15s ease',
        },
        sizeSmall: { padding: 4 },
        sizeMedium: { padding: 8 },
      },
    },

    // ── Inputs ──────────────────────────────────────────────────────────────
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.sm,
          fontSize: field.fontSize,
          lineHeight: field.lineHeight,
          backgroundColor: '#ffffff',
          transition: 'box-shadow 0.15s ease',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.colors.neutral[300],
            transition: 'border-color 0.15s ease',
          },
          '&:hover:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.colors.primary[400],
          },
          '&.Mui-focused': {
            boxShadow: `0 0 0 3px ${alpha(tokens.colors.primary[500], 0.12)}`,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: tokens.colors.primary[500],
              borderWidth: '1.5px',
            },
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.colors.error.main,
          },
          '&.Mui-disabled': {
            backgroundColor: tokens.colors.neutral[100],
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: tokens.colors.neutral[200],
            },
          },
        },


        adornedStart: { paddingLeft: field.paddingX },
        adornedEnd: { paddingRight: field.paddingX },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: field.fontSize,
          lineHeight: field.lineHeight,
          color: tokens.colors.neutral[600],
          '&.Mui-focused': { color: tokens.colors.primary[500] },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          fontSize: field.fontSize,
          lineHeight: field.lineHeight,
        },
        select: {
          padding: `${field.paddingY} ${field.paddingX} !important`,
          fontSize: field.fontSize,
          lineHeight: field.lineHeight,
          '&:focus': { backgroundColor: 'transparent' },
        },
        icon: {
          color: tokens.colors.neutral[500],
          right: 10,
          transition: 'transform 0.2s ease',
          '&.Mui-disabled': { color: tokens.colors.neutral[300] },
        },
        iconOpen: { transform: 'rotate(180deg)' },
      },
    },

    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: field.helperFontSize,
          lineHeight: field.helperLineHeight,
          marginTop: field.helperMarginTop,
          marginLeft: 0,
          marginRight: 0,
          '&.Mui-error': { color: tokens.colors.error.main },
        },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined' },
    },

    // ── Dialog Actions ──────────────────────────────────────────────────────
    MuiDialogActions: {
      styleOverrides: {
        root: {
          gap: 2,
          '& .MuiButton-root': { minWidth: 100, padding: '10px 24px' },
          '& .MuiButton-sizeSmall': { minWidth: 80 },
          '& .MuiButton-sizeLarge': { minWidth: 120 },
        },
      },
    },

    // ── Paper / Card ────────────────────────────────────────────────────────
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        elevation1: { boxShadow: '0 1px 3px rgba(15,23,42,0.08), 0 1px 6px rgba(15,23,42,0.04)' },
        elevation2: { boxShadow: '0 2px 6px rgba(15,23,42,0.08), 0 4px 12px rgba(15,23,42,0.06)' },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.lg,
          border: `1px solid ${tokens.colors.neutral[200]}`,
          boxShadow: '0 1px 3px rgba(15,23,42,0.08), 0 1px 6px rgba(15,23,42,0.04)',
          backgroundColor: '#fff',
        },
      },
    },

    // ── Popover ─────────────────────────────────────────────────────────────
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: tokens.radius.lg,
          boxShadow: '0 8px 24px rgba(15,23,42,0.12), 0 2px 8px rgba(15,23,42,0.08)',
          border: `1px solid ${tokens.colors.neutral[200]}`,
          backgroundImage: 'none',
        },
      },
    },

    // ── Tooltip ─────────────────────────────────────────────────────────────
    MuiTooltip: {
      defaultProps: { arrow: true },
      styleOverrides: {
        tooltip: {
          backgroundColor: tokens.colors.neutral[800],
          fontSize: '0.72rem',
          borderRadius: tokens.radius.xs,
          padding: '4px 10px',
          fontWeight: 400,
        },
        arrow: { color: tokens.colors.neutral[800] },
      },
    },

    // ── Chip ────────────────────────────────────────────────────────────────
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: tokens.radius.sm, fontSize: '0.72rem', fontWeight: 500, height: 22 },
        sizeSmall: { height: 20 },
      },
    },

    // ── List ────────────────────────────────────────────────────────────────
    MuiListSubheader: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
          fontWeight: 600,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: tokens.colors.neutral[500],
          lineHeight: '36px',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: { borderRadius: tokens.radius.xs },
      },
    },

    // ── Divider ─────────────────────────────────────────────────────────────
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: tokens.colors.neutral[200] },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.md,
          fontSize: '0.8125rem',
          border: `1px solid ${tokens.colors.neutral[200]}`,
          backgroundImage: 'none',
        },
        standardError: {
          backgroundColor: tokens.colors.error.light,
        },
        standardWarning: {
          backgroundColor: tokens.colors.warning.light,
        },
        standardInfo: {
          backgroundColor: tokens.colors.info.light,
        },
        standardSuccess: {
          backgroundColor: tokens.colors.success.light,
        },
      },
    },
  },
});

export default theme;